// The revision history of one triage-letter paragraph (TDI-408).
//
// Every change to a paragraph is one entry under
// triageDocuments.sections.{letterType}.editHistory[code][msTimestampKey]:
// who, when, the text before and after, and how it was made (`editType`).
// Two writers share the shape: the review page (manual_edit, reset,
// suggest_changes, polish, approved) and the server (regeneration, with a
// `reason`). This module reads that record for the history view and keeps the
// analytics bridge's input honest; it writes nothing.

/** How a change was made, in the words the history view uses. */
export const EDIT_TYPE_LABELS = {
  manual_edit: 'Edited by hand',
  reset: 'Reset to the draft',
  suggest_changes: 'Revised by AI from an instruction',
  polish: 'Tidied by AI after a manual edit',
  evidence_change: 'Redrafted by AI from adjusted evidence',
  approved: 'Approved',
  regeneration: 'Regenerated by AI',
};

const REGENERATION_REASON_LABELS = {
  initial_generation: 'Drafted by AI',
  section_regeneration: 'Regenerated by AI',
  background_generation: 'Drafted by AI',
};

/** The types that carry a `previousValue` and `newValue` (a change of text). */
export const TEXT_CHANGE_TYPES = new Set([
  'manual_edit', 'reset', 'suggest_changes', 'polish', 'evidence_change', 'regeneration',
]);

/**
 * Both writers key entries by milliseconds since the epoch, as a string, so
 * numeric order is write order. A key that is not a number sorts last.
 */
function keyOrder(key) {
  const text = String(key);
  return /^[0-9]+$/.test(text) ? Number(text) : Number.POSITIVE_INFINITY;
}

/** The history's entries, oldest first, each with its key attached. */
export function sortedHistoryEntries(history) {
  if (!history || typeof history !== 'object' || Array.isArray(history)) return [];
  return Object.keys(history)
    .filter((key) => history[key] && typeof history[key] === 'object' && !Array.isArray(history[key]))
    .sort((a, b) => keyOrder(a) - keyOrder(b) || String(a).localeCompare(String(b)))
    .map((key) => ({ key, ...history[key] }));
}

/** The `editType` of the most recent entry, or null for an untouched paragraph. */
export function lastEditType(history) {
  const entries = sortedHistoryEntries(history);
  if (!entries.length) return null;
  const last = entries[entries.length - 1];
  return last.editType == null ? null : String(last.editType);
}

/**
 * When an entry was written, as a Date, or null when it cannot be told.
 * Firestore returns a Timestamp (toDate), the page keeps a Date for entries it
 * has just written, and the key itself is the write time as a fallback.
 */
export function historyEntryDate(entry) {
  const stamp = entry?.timestamp;
  if (stamp && typeof stamp.toDate === 'function') {
    const date = stamp.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (stamp instanceof Date) return Number.isNaN(stamp.getTime()) ? null : stamp;
  if (typeof stamp === 'number') return new Date(stamp);
  const fromKey = keyOrder(entry?.key);
  return Number.isFinite(fromKey) ? new Date(fromKey) : null;
}

/** The one-line description of an entry for the history view. */
export function describeHistoryEntry(entry) {
  const editType = entry?.editType == null ? '' : String(entry.editType);
  if (editType === 'regeneration') {
    const reason = entry.reason == null ? '' : String(entry.reason);
    return REGENERATION_REASON_LABELS[reason] || EDIT_TYPE_LABELS.regeneration;
  }
  return EDIT_TYPE_LABELS[editType] || 'Changed';
}

/**
 * The history as the adoption analytics expect it: changes of text only.
 * An approval records who signed what and when, not a change of wording, and
 * the draft-recovery rule reads the first non-regeneration entry's
 * `previousValue` as the AI's draft; an approval there would recover an empty
 * draft and report the paragraph as rewritten in full.
 */
export function historyForAnalytics(history) {
  if (!history || typeof history !== 'object' || Array.isArray(history)) return {};
  const kept = {};
  Object.keys(history).forEach((key) => {
    const entry = history[key];
    if (!entry || typeof entry !== 'object') return;
    if (String(entry.editType || '') === 'approved') return;
    kept[key] = entry;
  });
  return kept;
}
